import { useRef, type KeyboardEvent } from 'react'
import { cx } from './cx'

export type Tab = { id: string; label: string }

type Props = {
  tabs: Tab[]
  active: string
  onChange: (id: string) => void
  /** id prefix used to link tabs with their panels (`${idBase}-panel-${tab}`). */
  idBase: string
  label?: string
  className?: string
}

/** Engraved tab labels on the window head line; arrow keys move between tabs. */
export function WindowTabs({ tabs, active, onChange, idBase, label = 'Разделы окна', className }: Props) {
  const refs = useRef<Array<HTMLButtonElement | null>>([])
  const onKey = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = tabs.length - 1
    let next = -1
    if (e.key === 'ArrowRight') next = index === last ? 0 : index + 1
    if (e.key === 'ArrowLeft') next = index === 0 ? last : index - 1
    if (e.key === 'Home') next = 0
    if (e.key === 'End') next = last
    if (next < 0) return
    e.preventDefault()
    onChange(tabs[next].id)
    refs.current[next]?.focus()
  }
  return (
    <div className={cx('wtabs', className)} role="tablist" aria-label={label}>
      {tabs.map((tab, i) => {
        const selected = tab.id === active
        return (
          <button
            key={tab.id}
            ref={el => { refs.current[i] = el }}
            type="button"
            role="tab"
            id={`${idBase}-tab-${tab.id}`}
            aria-selected={selected}
            aria-controls={`${idBase}-panel-${tab.id}`}
            tabIndex={selected ? 0 : -1}
            className={cx('wtab', selected && 'is-active')}
            onClick={() => onChange(tab.id)}
            onKeyDown={e => onKey(e, i)}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}
